import {
  type ChangeEvent,
  type ComponentProps,
  type ComponentType,
  type SVGProps,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { Alert } from "react-daisyui";
import {
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  MapPinIcon,
  PlusIcon,
  TicketIcon,
  TrashIcon,
  TrophyIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import TripCreateStep, {
  allTripCreateSteps,
} from "@/_constants/TripCreateStep.ts";
import AutocompleteInput from "@/components/functional/AutocompleteInput/AutocompleteInput.tsx";
import DateTimePickerInput from "@/components/functional/DateTimePickerInput/DateTimePickerInput.tsx";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import type { OnCompleteArgs } from "@/components/functional/TripManipulationForm/TripManipulationForm.tsx";
import { useStepperContext } from "@/components/shared/Stepper/StepperContext.tsx";
import StepIndicator from "@/components/ui/StepIndicator/StepIndicator.tsx";
import ChevronRightIcon from "@/components/ui/icons/ChevronRightIcon.tsx";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import {
  TripLayoutProvider,
  useTripLayoutContext,
} from "@/layouts/TripLayout/TripLayoutProvider.tsx";
import type { FixedBookingCategory } from "$/_const/trip/FixedBookingCategory.ts";
import type { TripData } from "$/models/Trip.model.ts";
import { tripCreateSchema } from "$/requests/TripCreateRequest.ts";
import dayjs from "$/utils/dayjs";
import { needsStartTimeReminder } from "@/utils/trip/needsStartTimeReminder.ts";
import { newFixedBooking } from "@/utils/trip/newFixedBooking.ts";

const isEndBeforeStart = (start: string, end: string) => {
  const [startDate, startTime] = start.split("T");
  const [endDate, endTime] = end.split("T");

  if (endDate !== startDate) return endDate < startDate;
  if (!startTime || !endTime) return false;

  return endTime < startTime;
};

const buildSchema = (tripStart?: string, tripEnd?: string) =>
  tripCreateSchema
    .pick({ what_has_been_booked: true })
    .superRefine(({ what_has_been_booked }, ctx) => {
      what_has_been_booked?.forEach((booking, index) => {
        if (!booking.dateTime?.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["what_has_been_booked", index, "dateTime"],
            message: "tripCreateForm.whatHasBeenBooked.errors.missingDate",
          });
        }

        if (
          booking.dateTime &&
          booking.end &&
          isEndBeforeStart(booking.dateTime, booking.end)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["what_has_been_booked", index, "end"],
            message: "tripCreateForm.whatHasBeenBooked.errors.endBeforeStart",
          });
        }

        // Arrival/departure boundary reasoning (late-night arrival, exact
        // departure time) depends on a real time-of-day, not just a date —
        // DateTimePickerInput can still emit a date-only value if the user
        // never picks a time, so flights require both dates AND their times.
        if (booking.bookingType === "flight") {
          if (!booking.dateTime?.includes("T")) {
            ctx.addIssue({
              code: "custom",
              path: ["what_has_been_booked", index, "dateTime"],
              message:
                "tripCreateForm.whatHasBeenBooked.errors.missingArrivalTime",
            });
          }

          if (!booking.end?.includes("T")) {
            ctx.addIssue({
              code: "custom",
              path: ["what_has_been_booked", index, "end"],
              message:
                "tripCreateForm.whatHasBeenBooked.errors.missingDepartureTime",
            });
          }
        }

        if (!tripStart || !tripEnd) return;

        ([
          ["dateTime", booking.dateTime?.slice(0, 10)],
          ["end", booking.end?.slice(0, 10)],
        ] as const).forEach(([field, value]) => {
          if (!value) return;
          if (value >= tripStart && value <= tripEnd) return;

          ctx.addIssue({
            code: "custom",
            path: ["what_has_been_booked", index, field],
            message:
              "tripCreateForm.whatHasBeenBooked.errors.outsideTripWindow",
          });
        });
      });
    });

export type whatHasBeenBookedSchema = z.infer<ReturnType<typeof buildSchema>>;

type BookingItem = NonNullable<
  whatHasBeenBookedSchema["what_has_been_booked"]
>[number];

type BookingType = BookingItem["bookingType"];
type FlightStatus = "booked" | "not_booked" | "not_needed";
type StayStatus = "all" | "some" | "none";

type FixedCategory = {
  bookingType: BookingType;
  category: FixedBookingCategory;
  defaultProvider: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
};

type WhatHasBeenBookedProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

type BookingInputProps = ComponentProps<"input"> & {
  error?: string;
  label: string;
};

const BookingInput = ({
  error,
  label,
  className = "",
  type = "text",
  ...props
}: BookingInputProps) => (
  <label className={`block min-w-0 font-sans ${className}`}>
    <span className="mb-1.5 block text-sm font-medium text-primary">
      {label}
    </span>
    <input
      {...props}
      type={type}
      aria-invalid={Boolean(error)}
      className={`h-12 w-full rounded-[10px] border bg-white px-4 font-sans text-base font-medium text-[#191919] outline-none placeholder:text-[#737373] focus:ring-2 focus:ring-primary/20 ${
        error ? "border-error" : "border-primary"
      }`}
      style={type === "date" || type === "time" || type === "datetime-local"
        ? { colorScheme: "light" }
        : undefined}
    />
    {error
      ? (
        <span className="mt-1.5 block text-sm text-error" role="alert">
          {error}
        </span>
      )
      : null}
  </label>
);

type BookingAutocompleteProps = {
  biasLocation?: string | null;
  className?: string;
  error?: string;
  includedPrimaryTypes?: ComponentProps<
    typeof AutocompleteInput
  >["includedPrimaryTypes"];
  restrictToBiasLocation?: boolean;
  label: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  value: string;
};

const BookingAutocomplete = ({
  biasLocation,
  className = "",
  error,
  includedPrimaryTypes = [],
  restrictToBiasLocation = false,
  label,
  onChange,
  placeholder,
  value,
}: BookingAutocompleteProps) => (
  <div className={`block min-w-0 font-sans ${className}`}>
    <span className="mb-1.5 block text-sm font-medium text-primary">
      {label}
    </span>
    <div
      className={`autocomplete-input flex h-12 w-full items-center rounded-[10px] border bg-white focus-within:ring-2 focus-within:ring-primary/20 ${
        error ? "border-error" : "border-primary"
      }`}
    >
      <AutocompleteInput
        biasLocation={biasLocation}
        includedPrimaryTypes={includedPrimaryTypes}
        restrictToBiasLocation={restrictToBiasLocation}
        includeDisplayName
        bordered={false}
        wrapperClassName="w-full"
        className="h-12 w-full border-0 bg-transparent px-4 font-sans text-base font-medium text-[#191919] outline-none placeholder:text-[#737373]"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
    </div>
    {error
      ? (
        <span className="mt-1.5 block text-sm text-error" role="alert">
          {error}
        </span>
      )
      : null}
  </div>
);

type BookingTextAreaProps = ComponentProps<"textarea"> & {
  error?: string;
  label: string;
};

const BookingTextArea = ({
  error,
  label,
  className = "",
  ...props
}: BookingTextAreaProps) => (
  <label className={`block min-w-0 font-sans ${className}`}>
    <span className="mb-1.5 block text-sm font-medium text-primary">
      {label}
    </span>
    <textarea
      {...props}
      aria-invalid={Boolean(error)}
      className={`min-h-24 w-full resize-y rounded-[10px] border bg-white px-4 py-3 font-sans text-base text-[#191919] outline-none placeholder:text-[#737373] focus:ring-2 focus:ring-primary/20 ${
        error ? "border-error" : "border-primary"
      }`}
    />
    {error
      ? (
        <span className="mt-1.5 block text-sm text-error" role="alert">
          {error}
        </span>
      )
      : null}
  </label>
);

const StatusButton = ({
  children,
  selected,
  onClick,
}: {
  children: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={`min-h-11 rounded-full border px-5 py-2 font-sans text-sm font-semibold transition-colors ${
      selected
        ? "border-[#16140f] bg-[#16140f] text-white"
        : "border-[#424242] bg-white text-[#191919] hover:border-primary hover:bg-primary/5"
    }`}
  >
    {children}
  </button>
);

function PageNavigation() {
  const { isMenuOpen, setIsMenuOpen } = useTripLayoutContext();

  return (
    <div
      className={`fixed inset-0 z-50 ${
        isMenuOpen
          ? "visible pointer-events-auto"
          : "invisible pointer-events-none"
      }`}
      aria-hidden={!isMenuOpen}
    >
      <button
        type="button"
        aria-label="Close navigation menu"
        className={`absolute inset-0 bg-black/45 transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      <TripLayout.NavigationPanel
        className={`absolute inset-y-0 left-0 transition-transform duration-300 ease-out ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      />
    </div>
  );
}

function WhatHasBeenBooked({
  onComplete,
  onCompleteWhenDirty,
}: WhatHasBeenBookedProps) {
  const { t } = useTranslation();
  const {
    nextStep,
    setContextData,
    contextData,
    previousStep,
    currentStepIndex,
    steps,
  } = useStepperContext<typeof allTripCreateSteps, TripData>();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const tripStart = contextData?.arrival_date?.slice(0, 10);
  const tripEnd = contextData?.departure_date?.slice(0, 10);
  const tripStartDate = tripStart
    ? dayjs(tripStart, "YYYY-MM-DD").toDate()
    : undefined;
  const tripEndDate = tripEnd
    ? dayjs(tripEnd, "YYYY-MM-DD").toDate()
    : undefined;

  const existingBookings = contextData?.what_has_been_booked ?? [];
  const [flightStatus, setFlightStatus] = useState<FlightStatus>(() =>
    existingBookings.some((booking) => booking.bookingType === "flight")
      ? "booked"
      : "not_booked"
  );
  const [stayStatus, setStayStatus] = useState<StayStatus>(() => {
    const numberOfStays = existingBookings.filter(
      (booking) => booking.bookingType === "stay",
    ).length;

    if (numberOfStays === 0) return "none";
    return numberOfStays > 1 ? "some" : "all";
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    getValues,
  } = useForm<whatHasBeenBookedSchema>({
    resolver: zodResolver(buildSchema(tripStart, tripEnd)),
    defaultValues: {
      what_has_been_booked: existingBookings,
    },
  });

  const errorText = (message?: string) =>
    message ? t(message, message) : undefined;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "what_has_been_booked",
  });

  const flightIndexes = fields.reduce<number[]>((indexes, booking, index) => {
    if (booking.bookingType === "flight") indexes.push(index);
    return indexes;
  }, []);
  const flightIndex = flightIndexes[0] ?? -1;

  const stayIndexes = fields.reduce<number[]>((indexes, booking, index) => {
    if (booking.bookingType === "stay") indexes.push(index);
    return indexes;
  }, []);

  const fixedIndexes = fields.reduce<number[]>((indexes, booking, index) => {
    if (booking.bookingType !== "flight" && booking.bookingType !== "stay") {
      indexes.push(index);
    }
    return indexes;
  }, []);

  const fixedCategories: FixedCategory[] = [
    {
      bookingType: "other",
      category: "wedding",
      defaultProvider: "A wedding",
      icon: CalendarDaysIcon,
      label: t(
        "tripCreateForm.whatHasBeenBooked.fixedCategories.wedding",
        "A wedding",
      ),
    },
    {
      bookingType: "other",
      category: "sporting_event",
      defaultProvider: "A sporting event",
      icon: TrophyIcon,
      label: t(
        "tripCreateForm.whatHasBeenBooked.fixedCategories.sport",
        "A sporting event",
      ),
    },
    {
      bookingType: "other",
      category: "concert",
      defaultProvider: "A concert",
      icon: TicketIcon,
      label: t(
        "tripCreateForm.whatHasBeenBooked.fixedCategories.concert",
        "A concert",
      ),
    },
    {
      bookingType: "attraction",
      category: "pre_booked_tour",
      defaultProvider: "A pre-booked tour",
      icon: MapPinIcon,
      label: t(
        "tripCreateForm.whatHasBeenBooked.fixedCategories.tour",
        "A pre-booked tour",
      ),
    },
    {
      bookingType: "other",
      category: "visiting_family_or_friends",
      defaultProvider: "Visiting family or friends",
      icon: UserGroupIcon,
      label: t(
        "tripCreateForm.whatHasBeenBooked.fixedCategories.family",
        "Visiting family or friends",
      ),
    },
    {
      bookingType: "restaurant",
      category: "restaurant_reservation",
      defaultProvider: "A restaurant reservation",
      icon: BuildingStorefrontIcon,
      label: t(
        "tripCreateForm.whatHasBeenBooked.fixedCategories.restaurant",
        "A restaurant reservation",
      ),
    },
  ];

  const selectFlightStatus = (status: FlightStatus) => {
    setFlightStatus(status);

    if (status === "booked") {
      if (flightIndexes.length === 0) {
        append({ id: crypto.randomUUID(), bookingType: "flight" });
      }
      return;
    }

    if (flightIndexes.length > 0) remove(flightIndexes);
  };

  const selectStayStatus = (status: StayStatus) => {
    setStayStatus(status);

    if (status === "none") {
      if (stayIndexes.length > 0) remove(stayIndexes);
      return;
    }

    if (stayIndexes.length === 0) {
      append({ id: crypto.randomUUID(), bookingType: "stay" });
    }
  };

  const selectFixedCategory = (
    index: number,
    category: FixedCategory,
  ) => {
    const currentBooking = getValues(
      `what_has_been_booked.${index}` as const,
    );

    update(index, {
      ...currentBooking,
      bookingType: category.bookingType,
      category: category.category,
      provider: category.defaultProvider,
    });
  };

  const onSubmit = (data: whatHasBeenBookedSchema) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TripCreateStep.WHAT_HAS_BEEN_BOOKED,
          data,
          context,
        };

        if (isDirty) {
          setContextData(() => context);
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError)),
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <TripLayoutProvider>
      <PageNavigation />

      <div
        className="min-h-[100dvh] max-w-none overflow-x-hidden bg-journi-theme-light text-[#191919]"
        style={{
          position: "relative",
          left: "50%",
          width: "100vw",
          minWidth: "100vw",
          maxWidth: "none",
          transform: "translateX(-50%)",
        }}
      >
        <header className="flex w-full items-center justify-between gap-6 border-b border-primary/60 px-5 py-5 sm:px-8 md:px-14 lg:px-20">
          <div className="flex items-center gap-4">
            <TripLayout.OpenMenuButton color="primary" />
            <span className="font-feature text-3xl font-bold">Journi</span>
          </div>

          <StepIndicator
            numberOfSteps={steps.length}
            currentStepIndex={currentStepIndex}
          />
        </header>

        <main className="w-full px-5 py-10 sm:px-8 md:px-[5vw] md:py-14">
          <form
            className="mx-auto flex w-full max-w-[1500px] flex-col"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            {error
              ? (
                <Alert status="error" className="mb-7">
                  {error.message}
                </Alert>
              )
              : null}

            <p className="text-center font-sans text-sm font-medium text-primary md:text-base">
              {t(
                "tripCreateForm.whatHasBeenBooked.eyebrow",
                "Fixed parts of the trip",
              )}
            </p>
            <h1 className="mt-3 font-feature text-4xl font-bold leading-tight md:text-5xl">
              {t(
                "tripCreateForm.whatHasBeenBooked.headerPrefix",
                "What have you",
              )}{" "}
              <span className="italic text-primary">
                {t(
                  "tripCreateForm.whatHasBeenBooked.headerHighlight",
                  "already booked?",
                )}
              </span>
            </h1>
            <p className="mt-5 max-w-[960px] rounded-[12px] border border-primary bg-primary/10 px-5 py-4 font-sans text-base leading-relaxed text-text-secondary">
              {t(
                "tripCreateForm.whatHasBeenBooked.info",
                "Give me the details of anything fixed and I'll build the trip around them. I'll never pretend to complete a booking for you.",
              )}
            </p>

            <section className="mt-10">
              <h2 className="font-sans text-lg font-medium text-text-secondary">
                {t(
                  "tripCreateForm.whatHasBeenBooked.transportHeading",
                  "Flights or transport",
                )}
              </h2>
              <div className="mt-4 flex flex-wrap gap-3">
                <StatusButton
                  selected={flightStatus === "booked"}
                  onClick={() => selectFlightStatus("booked")}
                >
                  {t(
                    "tripCreateForm.whatHasBeenBooked.flightStatus.booked",
                    "Already booked",
                  )}
                </StatusButton>
                <StatusButton
                  selected={flightStatus === "not_booked"}
                  onClick={() => selectFlightStatus("not_booked")}
                >
                  {t(
                    "tripCreateForm.whatHasBeenBooked.flightStatus.notBooked",
                    "Not booked yet",
                  )}
                </StatusButton>
                <StatusButton
                  selected={flightStatus === "not_needed"}
                  onClick={() => selectFlightStatus("not_needed")}
                >
                  {t(
                    "tripCreateForm.whatHasBeenBooked.flightStatus.notNeeded",
                    "No flights needed",
                  )}
                </StatusButton>
              </div>

              {flightStatus === "booked" && flightIndex >= 0
                ? (
                  <div className="mt-5 grid max-w-[1100px] gap-x-10 gap-y-5 md:grid-cols-2">
                    <h3 className="font-sans text-base font-semibold text-[#191919] md:col-span-2">
                      {t(
                        "tripCreateForm.whatHasBeenBooked.outboundFlight",
                        "Flight to your destination",
                      )}
                    </h3>
                    <Controller
                      control={control}
                      name={`what_has_been_booked.${flightIndex}.provider` as const}
                      render={({ field }) => (
                        <BookingInput
                          label={t(
                            "tripCreateForm.whatHasBeenBooked.outboundFlightNumber",
                            "Airline & flight no.",
                          )}
                          placeholder="e.g. Aer Lingus EI 402"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          error={errorText(
                            errors.what_has_been_booked?.[flightIndex]?.provider
                              ?.message,
                          )}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`what_has_been_booked.${flightIndex}.location` as const}
                      render={({ field }) => (
                        <BookingAutocomplete
                          biasLocation={contextData?.trip_destination}
                          includedPrimaryTypes={[
                            "airport",
                            "international_airport",
                          ]}
                          label={t(
                            "tripCreateForm.whatHasBeenBooked.arrivalAirport",
                            "Arrival airport",
                          )}
                          placeholder="e.g. Naples (NAP)"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          error={errorText(
                            errors.what_has_been_booked?.[flightIndex]?.location
                              ?.message,
                          )}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`what_has_been_booked.${flightIndex}.dateTime` as const}
                      render={({ field }) => (
                        <DateTimePickerInput
                          withTime
                          label={t(
                            "tripCreateForm.whatHasBeenBooked.arriving",
                            "Arrival date and local time",
                          )}
                          value={field.value}
                          onChange={field.onChange}
                          minDate={tripStartDate}
                          maxDate={tripEndDate}
                          error={errorText(
                            errors.what_has_been_booked?.[flightIndex]?.dateTime
                              ?.message,
                          )}
                        />
                      )}
                    />
                    <p className="-mt-2 font-feature text-sm italic text-text-secondary md:col-span-2">
                      {t(
                        "tripCreateForm.whatHasBeenBooked.arrivalHint",
                        "Enter when you land at your destination, not when you leave your home airport.",
                      )}
                    </p>
                    <h3 className="mt-3 font-sans text-base font-semibold text-[#191919] md:col-span-2">
                      {t(
                        "tripCreateForm.whatHasBeenBooked.returnFlight",
                        "Flight home or onward",
                      )}
                    </h3>
                    <Controller
                      control={control}
                      name={`what_has_been_booked.${flightIndex}.departureProvider` as const}
                      render={({ field }) => (
                        <BookingInput
                          label={t(
                            "tripCreateForm.whatHasBeenBooked.returnFlightNumber",
                            "Airline & flight no.",
                          )}
                          placeholder="e.g. Aer Lingus EI 403"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          error={errorText(
                            errors.what_has_been_booked?.[flightIndex]
                              ?.departureProvider?.message,
                          )}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`what_has_been_booked.${flightIndex}.departureLocation` as const}
                      render={({ field }) => (
                        <BookingAutocomplete
                          biasLocation={contextData?.trip_destination}
                          includedPrimaryTypes={[
                            "airport",
                            "international_airport",
                          ]}
                          label={t(
                            "tripCreateForm.whatHasBeenBooked.departureAirport",
                            "Departure airport",
                          )}
                          placeholder="e.g. Naples (NAP)"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          error={errorText(
                            errors.what_has_been_booked?.[flightIndex]
                              ?.departureLocation?.message,
                          )}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`what_has_been_booked.${flightIndex}.end` as const}
                      render={({ field }) => (
                        <DateTimePickerInput
                          withTime
                          label={t(
                            "tripCreateForm.whatHasBeenBooked.departing",
                            "Departure date and local time",
                          )}
                          value={field.value}
                          onChange={field.onChange}
                          minDate={tripStartDate}
                          maxDate={tripEndDate}
                          error={errorText(
                            errors.what_has_been_booked?.[flightIndex]?.end
                              ?.message,
                          )}
                        />
                      )}
                    />
                    <p className="-mt-2 font-feature text-sm italic text-text-secondary md:col-span-2">
                      {t(
                        "tripCreateForm.whatHasBeenBooked.departureHint",
                        "Enter the scheduled flight departure time, not the time you plan to leave your accommodation.",
                      )}
                    </p>
                  </div>
                )
                : null}
            </section>

            <section className="mt-10">
              <h2 className="font-sans text-lg font-medium text-text-secondary">
                {t(
                  "tripCreateForm.whatHasBeenBooked.accommodationHeading",
                  "Accommodation",
                )}
              </h2>
              <div className="mt-4 flex flex-wrap gap-3">
                <StatusButton
                  selected={stayStatus === "all"}
                  onClick={() => selectStayStatus("all")}
                >
                  {t(
                    "tripCreateForm.whatHasBeenBooked.stayStatus.all",
                    "All booked",
                  )}
                </StatusButton>
                <StatusButton
                  selected={stayStatus === "some"}
                  onClick={() => selectStayStatus("some")}
                >
                  {t(
                    "tripCreateForm.whatHasBeenBooked.stayStatus.some",
                    "Some nights booked",
                  )}
                </StatusButton>
                <StatusButton
                  selected={stayStatus === "none"}
                  onClick={() => selectStayStatus("none")}
                >
                  {t(
                    "tripCreateForm.whatHasBeenBooked.stayStatus.none",
                    "Nothing booked",
                  )}
                </StatusButton>
              </div>

              {stayStatus !== "none"
                ? (
                  <div className="mt-5 max-w-[1100px] space-y-7">
                    {stayIndexes.map((index, stayPosition) => (
                      <div
                        key={fields[index].id}
                        className="relative grid gap-x-10 gap-y-5 md:grid-cols-2"
                      >
                        {stayPosition > 0
                          ? (
                            <button
                              type="button"
                              aria-label={t(
                                "tripCreateForm.whatHasBeenBooked.removeProperty",
                                "Remove property",
                              )}
                              className="absolute right-0 top-0 text-error hover:opacity-70"
                              onClick={() => remove(index)}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )
                          : null}

                        <Controller
                          control={control}
                          name={`what_has_been_booked.${index}.provider` as const}
                          render={({ field }) => (
                            <BookingAutocomplete
                              biasLocation={contextData?.trip_destination}
                              includedPrimaryTypes={["hotel", "lodging"]}
                              restrictToBiasLocation
                              label={t(
                                "tripCreateForm.whatHasBeenBooked.propertyName",
                                "Property name",
                              )}
                              placeholder="e.g. Le Sirenuse"
                              value={field.value ?? ""}
                              onChange={field.onChange}
                              error={errorText(
                                errors.what_has_been_booked?.[index]?.provider
                                  ?.message,
                              )}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name={`what_has_been_booked.${index}.location` as const}
                          render={({ field }) => (
                            <BookingAutocomplete
                              biasLocation={contextData?.trip_destination}
                              includedPrimaryTypes={["(regions)"]}
                              restrictToBiasLocation
                              label={t(
                                "tripCreateForm.whatHasBeenBooked.area",
                                "Area",
                              )}
                              placeholder="e.g. Positano"
                              value={field.value ?? ""}
                              onChange={field.onChange}
                              error={errorText(
                                errors.what_has_been_booked?.[index]?.location
                                  ?.message,
                              )}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name={`what_has_been_booked.${index}.dateTime` as const}
                          render={({ field }) => (
                            <DateTimePickerInput
                              label={t(
                                "tripCreateForm.whatHasBeenBooked.checkIn",
                                "Check-in",
                              )}
                              value={field.value}
                              onChange={field.onChange}
                              minDate={tripStartDate}
                              maxDate={tripEndDate}
                              error={errorText(
                                errors.what_has_been_booked?.[index]?.dateTime
                                  ?.message,
                              )}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name={`what_has_been_booked.${index}.end` as const}
                          render={({ field }) => (
                            <DateTimePickerInput
                              label={t(
                                "tripCreateForm.whatHasBeenBooked.checkOut",
                                "Check-out",
                              )}
                              value={field.value}
                              onChange={field.onChange}
                              minDate={tripStartDate}
                              maxDate={tripEndDate}
                              error={errorText(
                                errors.what_has_been_booked?.[index]?.end
                                  ?.message,
                              )}
                            />
                          )}
                        />
                      </div>
                    ))}

                    <button
                      type="button"
                      className="inline-flex min-h-11 items-center gap-2 rounded-[10px] border border-dashed border-primary px-4 py-2 font-sans text-sm font-semibold text-[#191919] hover:bg-primary/5"
                      onClick={() =>
                        append({
                          id: crypto.randomUUID(),
                          bookingType: "stay",
                        })}
                    >
                      <PlusIcon className="h-4 w-4" />
                      {t(
                        "tripCreateForm.whatHasBeenBooked.addProperty",
                        "Add another property",
                      )}
                    </button>
                  </div>
                )
                : null}

              <p className="mt-4 font-feature text-base italic text-text-secondary">
                {t(
                  "tripCreateForm.whatHasBeenBooked.stayHint",
                  "I'll build activities around where you're staying, not the other way around.",
                )}
              </p>
            </section>

            <section className="mt-10">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-feature text-2xl font-bold">
                  {t(
                    "tripCreateForm.whatHasBeenBooked.anythingElse",
                    "Anything else fixed?",
                  )}
                </h2>
                <span className="rounded-full border border-primary px-2.5 py-1 font-sans text-[11px] font-semibold uppercase text-primary">
                  {t("label.optional", "Optional")}
                </span>
              </div>
              <p className="mt-2 font-sans text-sm text-text-secondary">
                {t(
                  "tripCreateForm.whatHasBeenBooked.anythingElseHint",
                  "Add any booking, event or commitment that Cody must plan around.",
                )}
              </p>

              <div className="mt-5 max-w-[1100px] rounded-[14px] border border-dashed border-primary p-5 md:p-7">
                <div className="space-y-8">
                  {fixedIndexes.map((index, fixedPosition) => {
                    const currentBooking = fields[index];

                    return (
                      <div
                        key={currentBooking.id}
                        className={fixedPosition > 0
                          ? "border-t border-primary/30 pt-8"
                          : ""}
                      >
                        <div className="flex flex-wrap gap-3">
                          {fixedCategories.map((category) => {
                            const selected = currentBooking.category
                              ? currentBooking.category === category.category
                              : currentBooking.bookingType ===
                                  category.bookingType &&
                                (category.bookingType !== "other" ||
                                  currentBooking.provider ===
                                    category.defaultProvider);
                            const Icon = category.icon;

                            return (
                              <button
                                key={`${index}-${category.defaultProvider}`}
                                type="button"
                                aria-pressed={selected}
                                className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-4 py-2 font-sans text-sm font-medium transition-colors ${
                                  selected
                                    ? "border-primary bg-primary/15 text-[#191919]"
                                    : "border-primary bg-white text-[#191919] hover:bg-primary/5"
                                }`}
                                onClick={() =>
                                  selectFixedCategory(index, category)}
                              >
                                <Icon className="h-4 w-4 text-primary" />
                                {category.label}
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-6 grid gap-x-10 gap-y-5 md:grid-cols-2">
                          <Controller
                            control={control}
                            name={`what_has_been_booked.${index}.provider` as const}
                            render={({ field }) => (
                              <BookingInput
                                className="md:col-span-2"
                                label={t(
                                  "tripCreateForm.whatHasBeenBooked.bookingEvent",
                                  "The booking or event",
                                )}
                                placeholder="e.g. A wedding"
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                error={errorText(
                                  errors.what_has_been_booked?.[index]?.provider
                                    ?.message,
                                )}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name={`what_has_been_booked.${index}.dateTime` as const}
                            render={({ field }) => (
                              <DateTimePickerInput
                                withTime
                                label={t(
                                  "tripCreateForm.whatHasBeenBooked.exactDateTime",
                                  "Date and start time",
                                )}
                                timeLabel={t(
                                  "tripCreateForm.whatHasBeenBooked.fields.start_time.label",
                                  "Start time",
                                )}
                                hint={needsStartTimeReminder({
                                    bookingType: currentBooking.bookingType,
                                    dateTime: field.value,
                                  })
                                  ? t(
                                    "tripCreateForm.whatHasBeenBooked.start_time_reminder",
                                  )
                                  : undefined}
                                value={field.value}
                                onChange={field.onChange}
                                minDate={tripStartDate}
                                maxDate={tripEndDate}
                                error={errorText(
                                  errors.what_has_been_booked?.[index]?.dateTime
                                    ?.message,
                                )}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name={`what_has_been_booked.${index}.location` as const}
                            render={({ field }) => (
                              <BookingAutocomplete
                                label={t(
                                  "tripCreateForm.whatHasBeenBooked.locationKnown",
                                  "Location, if known",
                                )}
                                placeholder="e.g. Positano"
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                error={errorText(
                                  errors.what_has_been_booked?.[index]?.location
                                    ?.message,
                                )}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name={`what_has_been_booked.${index}.bookingRef` as const}
                            render={({ field }) => (
                              <BookingInput
                                label={t(
                                  "tripCreateForm.whatHasBeenBooked.bookingReference",
                                  "Booking reference (optional)",
                                )}
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                error={errorText(
                                  errors.what_has_been_booked?.[index]
                                    ?.bookingRef?.message,
                                )}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name={`what_has_been_booked.${index}.notes` as const}
                            render={({ field }) => (
                              <BookingTextArea
                                className="md:col-span-2"
                                label={t(
                                  "tripCreateForm.whatHasBeenBooked.notes",
                                  "Anything else Cody should know (optional)",
                                )}
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                error={errorText(
                                  errors.what_has_been_booked?.[index]?.notes
                                    ?.message,
                                )}
                              />
                            )}
                          />
                        </div>

                        <button
                          type="button"
                          className="mt-4 inline-flex items-center gap-2 font-sans text-sm font-semibold text-error hover:opacity-70"
                          onClick={() => remove(index)}
                        >
                          <TrashIcon className="h-4 w-4" />
                          {t(
                            "tripCreateForm.whatHasBeenBooked.removeFixedItem",
                            "Remove fixed item",
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className={`inline-flex min-h-11 items-center gap-2 rounded-[10px] border border-dashed border-primary px-4 py-2 font-sans text-sm font-semibold text-[#191919] hover:bg-primary/5 ${
                    fixedIndexes.length > 0 ? "mt-7" : ""
                  }`}
                  onClick={() =>
                    append(newFixedBooking())}
                >
                  <PlusIcon className="h-5 w-5" />
                  {t(
                    "tripCreateForm.whatHasBeenBooked.addFixedItem",
                    "Add fixed item",
                  )}
                </button>
              </div>
            </section>

            <div className="mt-12 flex flex-col items-center gap-3 pb-8">
              <LoadingButton
                loading={isPending}
                type="submit"
                color="primary"
                endIcon={<ChevronRightIcon className="h-3.5 w-3.5" />}
                className="journi-primary-button w-full sm:w-[250px]"
              >
                {t("button.continue")}
              </LoadingButton>

              <button
                type="button"
                className="min-h-11 px-6 font-sans text-base font-semibold text-[#191919] hover:text-primary"
                onClick={previousStep}
              >
                {t("button.goBack")}
              </button>
            </div>
          </form>
        </main>
      </div>
    </TripLayoutProvider>
  );
}

export default WhatHasBeenBooked;
